/**
 * Nurse API functions
 */

import { apiClient } from '../api-client';

// ============= Type Definitions =============

export interface VitalsSubmission {
  patient_id: number;
  heart_rate: number;
  systolic_bp: number;
  diastolic_bp: number;
  spo2: number;
  temperature?: number;
  respiratory_rate?: number;
  weight?: number;
  height?: number;
  notes?: string;
}

export interface LabSampleSubmission {
  patient_id: number;
  sample_type: string;
  tube_type: string;
  volume: string;
  collection_time: string;
  lab_destination: string;
  requisition_number?: string;
  fasting_status?: string;
  special_instructions?: string;
  photo_url?: string;
}

export interface PatientStatusSubmission {
  patient_id: number;
  general_condition: string;
  mobility: string;
  breathing_difficulty: number; // 0-10 scale
  pain_level: number; // 0-10 scale
  adherence: string;
  mental_state?: string;
  skin_condition?: string;
  observations?: string;
  concerns?: string;
  recommended_actions?: string;
}

export const nursesApi = {
  /**
   * Get list of all nurses
   */
  getNurses: async (): Promise<any> => {
    return apiClient.get('/nurses');
  },

  /**
   * Get nurse by ID
   */
  getNurse: async (nurseId: number): Promise<any> => {
    return apiClient.get(`/nurses/${nurseId}`);
  },

  /**
   * Get nurse dashboard summary (current logged-in nurse)
   */
  getNurseDashboardSummary: async (): Promise<any> => {
    return apiClient.get('/nurses/me/summary');
  },

  /**
   * Get assigned patient for nurse
   */
  getAssignedPatient: async (): Promise<any> => {
    return apiClient.get('/nurses/me/assigned-patient');
  },

  /**
   * Submit vitals collected by nurse during home visit
   */
  submitVitals: async (data: VitalsSubmission): Promise<any> => {
    return apiClient.post('/nurses/vitals', data);
  },

  /**
   * Submit lab sample collection by nurse
   */
  submitLabSample: async (data: LabSampleSubmission): Promise<any> => {
    return apiClient.post('/nurses/lab-samples', data);
  },

  /**
   * Submit patient status assessment by nurse
   */
  submitPatientStatus: async (data: PatientStatusSubmission): Promise<any> => {
    return apiClient.post('/nurses/patient-status', data);
  },

  /**
   * ✅ NEW: Get patient vitals for viewing/editing
   */
  getPatientVitals: async (patientId: number, days: number = 30): Promise<any> => {
    return apiClient.get(`/vitals/patient/${patientId}`, { days });
  },

  /**
   * ✅ NEW: Update patient vitals (nurses can update any patient's vitals)
   */
  updatePatientVitals: async (vitalsId: number, data: Partial<VitalsSubmission>): Promise<any> => {
    return apiClient.put(`/vitals/${vitalsId}`, data);
  },

  /**
   * ✅ NEW: Delete patient vitals (nurses can delete any patient's vitals)
   */
  deletePatientVitals: async (vitalsId: number): Promise<any> => {
    return apiClient.delete(`/vitals/${vitalsId}`);
  },

  /**
   * ✅ NEW: Get patient medication logs for viewing/editing
   */
  getPatientMedicationLogs: async (patientId: number, days: number = 30): Promise<any> => {
    return apiClient.get(`/medications/patients/${patientId}/medications/history`, { days });
  },

  /**
   * ✅ NEW: Update patient medication log
   */
  updatePatientMedicationLog: async (logId: number, data: {
    scheduled_time?: string;
    taken_time?: string;
    status?: 'taken' | 'missed' | 'skipped';
    notes?: string;
  }): Promise<any> => {
    return apiClient.put(`/medications/me/medications/logs/${logId}`, data);
  },

  /**
   * ✅ NEW: Delete patient medication log
   */
  deletePatientMedicationLog: async (logId: number): Promise<any> => {
    return apiClient.delete(`/medications/me/medications/logs/${logId}`);
  },

  /**
   * Submit nurse task data (Vitals, Chest Drain, Nutrition, etc.)
   */
  submitNurseTaskData: async (data: {
    patient_id: number;
    task_category: string;
    tasks: Record<string, any>;
    recorded_at: string;
  }): Promise<any> => {
    return apiClient.post('/nurses/task-data', data);
  },

  /**
   * Get nurse task data for a patient
   */
  getNurseTaskData: async (
    patientId: number,
    taskCategory?: string,
    startDate?: string,
    endDate?: string,
    limit: number = 50
  ): Promise<any> => {
    const params: Record<string, any> = { limit };
    if (taskCategory) params.task_category = taskCategory;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return apiClient.get(`/nurses/task-data/${patientId}`, params);
  },

  /**
   * Update nurse task data
   */
  updateNurseTaskData: async (taskRecordId: number, data: {
    patient_id: number;
    task_category: string;
    tasks: Record<string, any>;
    recorded_at: string;
  }): Promise<any> => {
    return apiClient.put(`/nurses/task-data/${taskRecordId}`, data);
  },

  /**
   * Delete nurse task data
   */
  deleteNurseTaskData: async (taskRecordId: number): Promise<any> => {
    return apiClient.delete(`/nurses/task-data/${taskRecordId}`);
  },
};